import React, { useEffect, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { EIconTypes } from 'constants/Icons';
import { colors } from 'theme/colors';
import Icon from 'components/atoms/Icon/Icon';
import { EChipsVariants } from 'components/atoms/Chips';
import Grid from '@mui/material/Grid';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';
import { LEGAL_ID } from 'pages/Firearms/RegisteredFirearmsMapPage/FirearmsLocationMap';
import SearchFilterStore from 'state/SearchFilterStore';
import {
  ILegalEntityAddressDetail,
  ILegalEntityAddressDetailAccessories,
  ILegalEntityAddressDetailAmmunition,
  ILegalEntityAddressDetailComponents,
  ILegalEntityAddressDetailFirearms,
} from 'models/legalEntity';
import { StyledChips, StyledCloseIcon, StyledItem, StyledSidebarWrapper } from './style';

export enum ELegalityFilter {
  legal = 'd90f05bf-6baf-4f86-9ca3-9501af6901ea',
  illegal = 'ae5de668-67ac-4650-af26-23dd0cb97c89',
}

interface IAllDataSidebar {
  handleClose: () => void;
}

const AllDataSidebar = ({ handleClose }: IAllDataSidebar) => {
  const { t } = useTranslations();
  const { getState } = useDictionary();
  const { selectedLanguage } = useLang();
  const [assets, setAssets] = useState<ILegalEntityAddressDetail>();
  const [filteredAncillaries, setFilteredAncillaries] =
    useState<ILegalEntityAddressDetail | null>(null);
  const [filteredAccessories, setFilteredAccessories] = useState<
    ILegalEntityAddressDetailAccessories[]
  >([]);
  const [filteredAmmunition, setFilteredAmmunition] = useState<
    ILegalEntityAddressDetailAmmunition[]
  >([]);
  const [filteredComponents, setFilteredComponents] = useState<
    ILegalEntityAddressDetailComponents[]
  >([]);
  const [filteredFirearms, setFilteredFirearms] = useState<
    ILegalEntityAddressDetailFirearms[]
  >([]);
  const [chipsData, setChipsData] = useState<{ illegal: number; legal: number }>({
    illegal: 0,
    legal: 0,
  });
  const [filter, setFilter] = useState<ELegalityFilter>();

  useEffect(() => {
    (async function initDictionaries() {
      getState();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const prepareAssets = (assetsData: ILegalEntityAddressDetail) => {
    let illegalCount = 0;
    let legalCount = 0;

    let filteredItems: ILegalEntityAddressDetail = assetsData;

    if (filter) {
      filteredItems = {
        accessories: assetsData.accessories.filter(
          (ancillary) => ancillary.legalityUniqueId === filter,
        ),
        firearms: assetsData.firearms.filter(
          (ancillary) => ancillary.legalityUniqueId === filter,
        ),
        ammunition: assetsData.ammunition.filter(
          (ancillary) => ancillary.legalityUniqueId === filter,
        ),
        components: assetsData.components.filter(
          (ancillary) => ancillary.legalityUniqueId === filter,
        ),
      };
    }
    setFilteredFirearms(filteredItems.firearms);
    setFilteredAccessories(filteredItems.accessories);
    setFilteredAmmunition(filteredItems.ammunition);
    setFilteredComponents(filteredItems.components);

    [
      ...filteredItems.accessories,
      ...filteredItems.firearms,
      ...filteredItems.ammunition,
      ...filteredItems.components,
    ].map((item) => {
      if (item.legalityUniqueId !== LEGAL_ID) {
        illegalCount++;
      } else {
        legalCount++;
      }
      return false;
    });

    setFilteredAncillaries(filteredItems);
    setChipsData({ illegal: illegalCount, legal: legalCount });
  };

  document.addEventListener('setAssetsEvent', (data: any) => {
    const assetsData: ILegalEntityAddressDetail = data.detail
      .data as ILegalEntityAddressDetail;
    setAssets(assetsData);
    prepareAssets(assetsData);
    setFilter(undefined);
  });

  useEffect(() => {
    if (assets) {
      prepareAssets(assets);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  if (!filteredAncillaries) {
    return null;
  }

  return (
    <StyledSidebarWrapper id="accessories-data-sidebar">
      <StyledCloseIcon onClick={handleClose}>
        <Icon type={EIconTypes.close} color={colors.black} />
      </StyledCloseIcon>
      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
        {t('assets.location.legality')}
      </Typography>
      <Grid container size={{ xs: 12 }} columnSpacing={1}>
        {chipsData.legal > 0 && (
          <Grid size={{ xs: 6 }}>
            <StyledChips
              text={`${t('assets.location.legal.label')}: ${chipsData.legal}`}
              variant={EChipsVariants.success}
              fullWidth
            />
          </Grid>
        )}
        {chipsData.illegal > 0 && (
          <Grid size={{ xs: 6 }}>
            <StyledChips
              text={`${t('assets.location.illegal.label')}: ${chipsData.illegal}`}
              variant={EChipsVariants.error}
              fullWidth
            />
          </Grid>
        )}
      </Grid>

      <Grid
        container
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '16px 0',
          paddingTop: '16px',
          borderTop: {
            xs: `1px solid ${colors.gray200}`,
          },
        }}
      >
        {filteredAccessories && filteredAccessories.length > 0 && (
          <StyledItem
            onClick={() => {
              SearchFilterStore.setAccessories(filteredAccessories);
            }}
          >
            <Icon type={EIconTypes.accessory} fontSize={32} />

            <Stack>
              <Typography variant="subtitle2">
                {t('assets.location.accessories.label')}
              </Typography>
              <Typography variant="caption">{`${t('global.totalNumber.label')}: ${filteredAccessories.length
                }`}</Typography>
            </Stack>
            <Icon type={EIconTypes.right} />
          </StyledItem>
        )}
        {filteredAmmunition && filteredAmmunition.length > 0 && (
          <StyledItem
            onClick={() => {
              SearchFilterStore.setAmmunition(filteredAmmunition);
            }}
          >
            <Icon type={EIconTypes.ammunition} fontSize={32} />

            <Stack>
              <Typography variant="subtitle2">
                {t('assets.location.ammunition.label')}
              </Typography>
              <Typography variant="caption">{`${t('global.totalNumber.label')}: ${filteredAmmunition.length
                }`}</Typography>
            </Stack>
            <Icon type={EIconTypes.right} />
          </StyledItem>
        )}
        {filteredComponents && filteredComponents.length > 0 && (
          <StyledItem
            onClick={() => {
              SearchFilterStore.setComponents(filteredComponents);
            }}
          >
            <Icon type={EIconTypes.component} fontSize={32} />

            <Stack>
              <Typography variant="subtitle2">
                {t('assets.location.components.label')}
              </Typography>
              <Typography variant="caption">{`${t('global.totalNumber.label')}: ${filteredComponents.length
                }`}</Typography>
            </Stack>
            <Icon type={EIconTypes.right} />
          </StyledItem>
        )}
        {filteredFirearms && filteredFirearms.length > 0 && (
          <StyledItem
            onClick={() => {
              SearchFilterStore.setFirearms(filteredFirearms);
            }}
          >
            <Icon type={EIconTypes.firearms} fontSize={32} />

            <Stack>
              <Typography variant="subtitle2">
                {t('assets.location.firearms.label')}
              </Typography>
              <Typography variant="caption">{`${t('global.totalNumber.label')}: ${filteredFirearms.length
                }`}</Typography>
            </Stack>
            <Icon type={EIconTypes.right} />
          </StyledItem>
        )}
      </Grid>
    </StyledSidebarWrapper>
  );
};

export default AllDataSidebar;
